import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, InputNumber, Modal, Select } from "antd";
import styled, { css } from "styled-components";
import configFile from "config.json";
import axios from "utils/axios";
import { NotificationManager } from "react-notifications";
import { BsSearch, BsXCircle } from "react-icons/bs";
import { SaveOutlined } from "@ant-design/icons"

const API_BASE = `${configFile.PROD_SERVER_URL}`;
const GROUP_TYPE_OPTIONS = [
  { value: "main", label: "main" },
  { value: "side", label: "side" },
];
const MENU_TYPE_OPTIONS = [
  { value: "pizza", label: "pizza" },
  { value: "single", label: "single" },
];

const resolveItemId = (item) =>
  item?.id ??
  item?.definitionId ??
  item?.menuDefinitionId ??
  item?.definition?.id ??
  item?.menuId ??
  item?.masterId ??
  item?.comboMenuId ??
  item?.masterObjNum;

const toSafeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getMenuDefinitionsFromResponse = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  const [firstKey] = Object.keys(data || {});
  return data?.[firstKey] || [];
};

const getAdditionalPrice = (item) => toSafeNumber(item?.price, 0);

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const formatUser = (value) => {
  if (!value) return "-";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return (
      value.fullName ||
      value.name ||
      value.username ||
      value.email ||
      value.id ||
      "-"
    );
  }
  return "-";
};

const getCreatedAt = (item) => item?.createdAt ?? item?.created_at ?? null;
const getUpdatedAt = (item) => item?.updatedAt ?? item?.updated_at ?? null;
const getUpdatedBy = (item) => item?.updatedBy ?? item?.updated_by ?? null;

const getDefinitionDiff = (currentItems = [], initialItems = []) => {
  const currentMap = new Map();
  const initialMap = new Map();

  currentItems.forEach((item) => {
    const id = resolveItemId(item);
    if (id) {
      currentMap.set(id, item);
    }
  });

  initialItems.forEach((item) => {
    const id = resolveItemId(item);
    if (id) {
      initialMap.set(id, item);
    }
  });

  const added = [];
  const removed = [];
  const modified = [];

  currentMap.forEach((item, id) => {
    if (!initialMap.has(id)) {
      added.push(item);
      return;
    }
    const initialItem = initialMap.get(id);
    if (getAdditionalPrice(item) !== getAdditionalPrice(initialItem)) {
      modified.push({
        current: item,
        initial: initialItem,
      });
    }
  });

  initialMap.forEach((item, id) => {
    if (!currentMap.has(id)) {
      removed.push(item);
    }
  });

  return { added, removed, modified };
};

const getComboGroupRoute = (groupPayload = {}) =>
  groupPayload?.id || groupPayload?.comboGroupId
    ? "updateComboGroup"
    : "createComboGroup";

const ConfigTab = ({ deal = {}, tagOptions = [] }) => {
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedItemsByGroup, setSelectedItemsByGroup] = useState({});
  const [initialItemsByGroup, setInitialItemsByGroup] = useState({});
  const [groups, setGroups] = useState(deal.groups || []);
  const [selectedAvailableIds, setSelectedAvailableIds] = useState([]);
  const [selectedChosenIds, setSelectedChosenIds] = useState([]);
  const [newStepName, setNewStepName] = useState("");
  const [newStepType, setNewStepType] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [isSavingDefinitions, setIsSavingDefinitions] = useState(false);
  const fetchGroupData = useCallback(async (groupId) => {
    try {
      if (Object.prototype.hasOwnProperty.call(selectedItemsByGroup, groupId)) {
        return;
      }
      const { data: result } = await axios.post(`${API_BASE}/menu2/combo/getComboGroupMenus`, { groupId });
      const items = Object.values(result?.data || {});
      const definitions = items.flatMap((entry) => entry?.definitions || []);
      setSelectedItemsByGroup((prev) => ({ ...prev, [groupId]: definitions }));
      setInitialItemsByGroup((prev) => ({ ...prev, [groupId]: definitions }));
    } catch (err) {
      console.log(err);
    }
  }, [selectedItemsByGroup]);
  const [menuData, setMenuData] = useState([]);
  const [majorGroupOptions, setMajorGroupOptions] = useState([]);
  const [majorGroupId, setMajorGroupId] = useState(undefined);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedType, setSelectedType] = useState(undefined);
  const [selectedSizeId, setSelectedSizeId] = useState(undefined);
  const [selectedBaseIds, setSelectedBaseIds] = useState([]);
  const [availableSearchTerm, setAvailableSearchTerm] = useState("");
  const [sizes, setSizes] = useState([]);
  const [bases, setBases] = useState([]);
  const resetSelections = useCallback(() => {
    setSelectedAvailableIds([]);
    setSelectedChosenIds([]);
  }, []);
  const fetchSizeData = useCallback(async () => {
    try {
      const { data: response } = await axios.post(`${API_BASE}/menu2/option/listSizes`);
      setSizes(response.data || []);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchBaseData = useCallback(async () => {
    try {
      const { data: response } = await axios.post(`${API_BASE}/menu2/option/listBases`);
      setBases(response.data || []);
    } catch (err) {
      console.log(err);
    }
  }, []);
  const fetchMenuData = useCallback(async () => {
    try {
      resetSelections();
      const { data: result } = await axios.post(
        `${API_BASE}/menu2/menu/getMenuDefinitions`,
        { majorGroupId, tagIds: selectedTagIds, sizeId: selectedSizeId, baseIds: selectedBaseIds }
      );
      setMenuData(getMenuDefinitionsFromResponse(result?.data));
    } catch (err) {
      console.log(err);
    }
  }, [majorGroupId, resetSelections, selectedBaseIds, selectedSizeId, selectedTagIds]);
  const fetchMajorGroups = useCallback(async () => {
    try {
      const { data: result } = await axios.post(`${API_BASE}/menu2/option/listMajorGroups`);
      setMajorGroupOptions(result?.data || []);
    } catch (err) {
      console.log(err);
    }
  }, []);

  const selectedItems = useMemo(
    () => selectedItemsByGroup[selectedGroupId] || [],
    [selectedGroupId, selectedItemsByGroup]
  );
  const initialSelectedItems = useMemo(
    () => initialItemsByGroup[selectedGroupId] || [],
    [initialItemsByGroup, selectedGroupId]
  );
  const getSelectedId = resolveItemId;
  const getAvailableId = (item) => item?.id;
  const selectedIds = useMemo(() => {
    const ids = selectedItems.map(getSelectedId).filter(Boolean);
    return new Set(ids);
  }, [getSelectedId, selectedItems]);
  const selectedAvailableIdSet = useMemo(
    () => new Set(selectedAvailableIds),
    [selectedAvailableIds]
  );
  const selectedChosenIdSet = useMemo(
    () => new Set(selectedChosenIds),
    [selectedChosenIds]
  );
  const filteredMenuData = useMemo(
    () =>
      menuData.filter((item) => {
        if (selectedIds.has(getAvailableId(item))) {
          return false;
        }
        if (selectedType && String(item?.type || "").toLowerCase() !== selectedType) {
          return false;
        }
        if (availableSearchTerm) {
          const term = availableSearchTerm.toLowerCase();
          const name = (item.masterName || item.name || "").toLowerCase();
          if (!name.includes(term)) {
            return false;
          }
        }
        return true;
      }),
    [menuData, selectedIds, selectedType, availableSearchTerm]
  );
  const currentGroupDiff = useMemo(
    () => getDefinitionDiff(selectedItems, initialSelectedItems),
    [initialSelectedItems, selectedItems]
  );
  const hasPendingChanges =
    currentGroupDiff.added.length > 0 ||
    currentGroupDiff.removed.length > 0 ||
    currentGroupDiff.modified.length > 0;

  const majorGroupSelectOptions = useMemo(
    () => majorGroupOptions.map((option) => ({ label: option.name, value: option.id })),
    [majorGroupOptions]
  );
  const tagSelectOptions = useMemo(
    () =>
      tagOptions.map((tag) => ({
        label: tag.slug || tag.name || tag.id,
        value: tag.id || tag.slug,
      })),
    [tagOptions]
  );
  const sizeSelectOptions = useMemo(
    () =>
      sizes.map((size) => ({
        label: size.name || size.sizeName || size.id,
        value: size.id,
      })),
    [sizes]
  );
  const baseSelectOptions = useMemo(
    () =>
      bases.map((base) => ({
        label: base.name || base.baseName || base.id,
        value: base.id,
      })),
    [bases]
  );
  const hasMainGroup = useMemo(
    () => groups.some((group) => String(group?.groupType || "").toLowerCase() === "main"),
    [groups]
  );
  const groupTypeSelectOptions = useMemo(
    () =>
      GROUP_TYPE_OPTIONS.map((option) => ({
        ...option,
        disabled: option.value === "main" && hasMainGroup,
      })),
    [hasMainGroup]
  );

  useEffect(() => {
    fetchMajorGroups();
    fetchBaseData();
    fetchSizeData();
  }, [fetchBaseData, fetchMajorGroups, fetchSizeData]);

  useEffect(() => {
    setGroups(deal.groups || []);
    setSelectedItemsByGroup({});
    setInitialItemsByGroup({});
  }, [deal]);

  useEffect(() => {
    if (groups.length === 0) {
      setSelectedGroupId(null);
      return;
    }
    const isSelectedValid = groups.some((group) => group.id === selectedGroupId);
    if (!isSelectedValid) {
      setSelectedGroupId(groups[0]?.id ?? null);
    }
  }, [groups, selectedGroupId]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchGroupData(selectedGroupId);
    }
  }, [fetchGroupData, selectedGroupId]);

  useEffect(() => {
    resetSelections();
  }, [resetSelections, selectedGroupId]);

  const handleStepSelect = useCallback((groupId) => {
    setSelectedGroupId(groupId);
    fetchGroupData(groupId);
  }, [fetchGroupData]);

  const addStep = useCallback(async () => {
    const name = newStepName.trim();
    if (!name) {
      return;
    }
    if (!newStepType) {
      NotificationManager.warning("Алхамын төрөл сонгоно уу.");
      return;
    }
    if (String(newStepType).toLowerCase() === "main" && hasMainGroup) {
      NotificationManager.warning("Зөвхөн нэг main group үүсгэх боломжтой.");
      return;
    }
    const newGroup = {
      comboId: deal?.id,
      name,
      groupType: newStepType,
    };
    try {
      const route = getComboGroupRoute(newGroup);
      const { data: result } = await axios.post(`${API_BASE}/menu2/manageCombo/${route}`, newGroup);
      const groupToAdd = result?.data || {};
      setGroups((prev) => [...prev, groupToAdd]);
      setSelectedGroupId(groupToAdd.id);
      setSelectedItemsByGroup((prev) => ({ ...prev, [groupToAdd.id]: [] }));
      setInitialItemsByGroup((prev) => ({ ...prev, [groupToAdd.id]: [] }));
      setNewStepName("");
      setNewStepType("");
    } catch (err) {
      console.log(err);
      NotificationManager.error(err?.response?.data?.errorMessage || 'Алдаа гарлаа')
    }
  }, [deal?.id, hasMainGroup, newStepName, newStepType]);

  const toggleSelection = useCallback((itemId, setSelection) => {
    setSelection((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  }, []);
  const handleListItemKeyDown = (event, itemId, setSelection) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    toggleSelection(itemId, setSelection);
  };
  const updateSelectedItemValue = (itemId, nextValue) => {
    if (!selectedGroupId) {
      return;
    }
    const nextPrice = toSafeNumber(nextValue, 0);
    setSelectedItemsByGroup((prev) => {
      const existing = prev[selectedGroupId] || [];
      return {
        ...prev,
        [selectedGroupId]: existing.map((entry) => {
          if (getSelectedId(entry) !== itemId) {
            return entry;
          }
          return {
            ...entry,
            price: nextPrice,
          };
        }),
      };
    });
  };

  const moveSelectedToGroup = useCallback(() => {
    if (!selectedGroupId || selectedAvailableIds.length === 0) {
      return;
    }
    const itemsToMove = filteredMenuData.filter((item) =>
      selectedAvailableIds.includes(getAvailableId(item))
    );
    const newItems = itemsToMove.filter((item) => !selectedIds.has(getAvailableId(item)));
    if (newItems.length === 0) {
      setSelectedAvailableIds([]);
      return;
    }
    setSelectedItemsByGroup((prev) => {
      const existing = prev[selectedGroupId] || [];
      return { ...prev, [selectedGroupId]: [...existing, ...newItems] };
    });
    setSelectedAvailableIds([]);
  }, [filteredMenuData, selectedAvailableIds, selectedGroupId, selectedIds]);

  const moveSelectedToAvailable = useCallback(() => {
    if (!selectedGroupId || selectedChosenIds.length === 0) {
      return;
    }
    const itemsToRemove = selectedItems.filter((item) =>
      selectedChosenIds.includes(getSelectedId(item))
    );
    if (itemsToRemove.length === 0) {
      setSelectedChosenIds([]);
      return;
    }
    setSelectedItemsByGroup((prev) => {
      const existing = prev[selectedGroupId] || [];
      const removeIds = new Set(selectedChosenIds);
      return { ...prev, [selectedGroupId]: existing.filter((entry) => !removeIds.has(getSelectedId(entry))) };
    });
    setSelectedChosenIds([]);
  }, [getSelectedId, selectedChosenIds, selectedGroupId, selectedItems]);

  const saveDefinitionChanges = useCallback(async () => {
    if (!selectedGroupId || !hasPendingChanges || isSavingDefinitions) {
      return;
    }
    setIsSavingDefinitions(true);
    try {
      if (currentGroupDiff.removed.length > 0) {
        await Promise.all(
          currentGroupDiff.removed.map((item) =>
            axios.post(`${API_BASE}/menu2/manageCombo/removeDefinitionCombo`, {
              comboGroupId: selectedGroupId,
              definitionId: resolveItemId(item),
            })
          )
        );
      }

      if (currentGroupDiff.added.length > 0) {
        await axios.post(`${API_BASE}/menu2/manageCombo/addDefinitionCombo`, {
          definitions: currentGroupDiff.added.map((item) => ({
            comboGroupId: selectedGroupId,
            definitionId: resolveItemId(item),
            price: getAdditionalPrice(item),
          })),
        });
      }

      if (currentGroupDiff.modified.length > 0) {
        await axios.post(`${API_BASE}/menu2/manageCombo/updateDefinitionCombo`, {
          definitions: currentGroupDiff.modified.map(({ current }) => ({
            comboGroupId: selectedGroupId,
            definitionId: resolveItemId(current),
            price: getAdditionalPrice(current),
          })),
        });
      }

      setInitialItemsByGroup((prev) => ({
        ...prev,
        [selectedGroupId]: selectedItems.map((item) => ({ ...item })),
      }));
      resetSelections();
      NotificationManager.success("Амжилттай хадгаллаа");
    } catch (err) {
      console.log(err);
      NotificationManager.error(err?.response?.data?.errorMessage || "Алдаа гарлаа");
    } finally {
      setIsSavingDefinitions(false);
    }
  }, [
    currentGroupDiff.added,
    currentGroupDiff.modified,
    currentGroupDiff.removed,
    hasPendingChanges,
    isSavingDefinitions,
    resetSelections,
    selectedGroupId,
    selectedItems,
  ]);

  const openDeleteModal = useCallback((group) => {
    setGroupToDelete(group);
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setGroupToDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!groupToDelete) {
      return;
    }
    try {
      await axios.post(`${API_BASE}/menu2/manageCombo/deleteComboGroup`, { comboGroupId: groupToDelete.id });
      const remainingGroups = groups.filter((group) => group.id !== groupToDelete.id);
      const nextGroupId = remainingGroups[0]?.id || null;
      setGroups(remainingGroups);
      setSelectedItemsByGroup((prev) => {
        const next = { ...prev };
        delete next[groupToDelete.id];
        return next;
      });
      setInitialItemsByGroup((prev) => {
        const next = { ...prev };
        delete next[groupToDelete.id];
        return next;
      });
      resetSelections();
      setSelectedGroupId(nextGroupId);
      if (nextGroupId) {
        fetchGroupData(nextGroupId);
      }
      fetchMenuData();
    } catch (err) {
      console.log(err);
    } finally {
      closeDeleteModal();
    }
  }, [closeDeleteModal, fetchGroupData, fetchMenuData, groupToDelete, groups, resetSelections]);

  return (
    <Wrap>
      <Modal
        title="Алхам устгах уу?"
        open={deleteModalOpen}
        onOk={confirmDelete}
        onCancel={closeDeleteModal}
        okText="Тийм"
        cancelText="Болих"
        okButtonProps={{ danger: true }}
      >
        <p>
          "{groupToDelete?.name || "Сонгосон алхам"}" алхамыг устгахдаа итгэлтэй
          байна уу?
        </p>
      </Modal>
      <TopRow>
        <StepRow>
          {groups.map((group) => (
            <StepCard
              key={group.id}
              $active={group.active || selectedGroupId === group.id}
              role="button"
              tabIndex={0}
              onClick={() => handleStepSelect(group.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleStepSelect(group.id);
                }
              }}
            >
              <StepInfo>
                <StepBadge>{group.name}</StepBadge>
              </StepInfo>
              <span>{group.groupType}</span>
              <StepDelete
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  openDeleteModal(group);
                }}
              >
                x
              </StepDelete>
            </StepCard>
          ))}
          <StepCardDashed>
            <StepForm>
              <Input
                size="small"
                placeholder="Нэр"
                value={newStepName}
                onChange={(event) => setNewStepName(event.target.value)}
              />
              <Select
                size="small"
                placeholder="Төрөл"
                value={newStepType || undefined}
                onChange={(value) => setNewStepType(value)}
                options={groupTypeSelectOptions}
              />
              <Button size="small" type="primary" onClick={addStep}>
                +
              </Button>
            </StepForm>
          </StepCardDashed>
        </StepRow>
      </TopRow>
      <FiltersRow>
        <FilterGroup>
          <Select
            placeholder="Ангилал"
            options={majorGroupSelectOptions}
            allowClear
            onChange={(value) => setMajorGroupId(value)}
            onClear={() => setMajorGroupId(undefined)}
          />
        </FilterGroup>
        <FilterGroup>
          <Select
            mode="multiple"
            placeholder="Tags"
            options={tagSelectOptions}
            value={selectedTagIds}
            onChange={setSelectedTagIds}
            allowClear
          />
        </FilterGroup>
        <FilterGroup>
          <Select
            placeholder="Type"
            options={MENU_TYPE_OPTIONS}
            value={selectedType}
            onChange={setSelectedType}
            allowClear
            onClear={() => setSelectedType(undefined)}
          />
        </FilterGroup>
        <FilterGroup>
          <Select
            placeholder="Size"
            options={sizeSelectOptions}
            value={selectedSizeId}
            onChange={setSelectedSizeId}
            allowClear
          />
        </FilterGroup>
        <FilterGroup>
          <Select
            mode="multiple"
            placeholder="Base"
            options={baseSelectOptions}
            value={selectedBaseIds}
            onChange={setSelectedBaseIds}
            allowClear
          />
        </FilterGroup>
        <Button type="primary" onClick={fetchMenuData}><BsSearch /></Button>
      </FiltersRow>
      <TransferWrap>
        <ListPanel>
          <PanelHeader>Бүтээгдэхүүний жагсаалт</PanelHeader>
          <SearchInputWrap>
            <BsSearch className="search-icon" />
            <input
              type="text"
              placeholder="Нэрээр хайх..."
              value={availableSearchTerm}
              onChange={(e) => setAvailableSearchTerm(e.target.value)}
            />
            {availableSearchTerm && (
              <BsXCircle
                className="clear-icon"
                onClick={() => setAvailableSearchTerm("")}
              />
            )}
          </SearchInputWrap>
          <PanelBody>
            {!selectedGroupId && <EmptyPanel>Алхам сонгоод бараа сонгоно уу.</EmptyPanel>}
            {selectedGroupId && filteredMenuData.length === 0 && <EmptyPanel>Бараа олдсонгүй.</EmptyPanel>}
            {selectedGroupId && filteredMenuData.map((item) => (
              <ListItem
                key={getAvailableId(item)}
                role="button"
                tabIndex={0}
                $selected={selectedAvailableIdSet.has(getAvailableId(item))}
                onClick={() => toggleSelection(getAvailableId(item), setSelectedAvailableIds)}
                onKeyDown={(event) =>
                  handleListItemKeyDown(event, getAvailableId(item), setSelectedAvailableIds)
                }
              >
                <div>
                  <ListTitle>
                    <span>{item.masterName || item.name || "Нэргүй бараа"}</span>
                  </ListTitle>
                  <ListMeta>
                    <span>MI: {item.miMasterObjNum || "-"}</span>
                    {item.description && <span>{item.description}</span>}
                  </ListMeta>
                </div>
                <ListTitle>
                  <span className="type_blue_highlight">{item.type || item.categoryName || "-"}</span>
                </ListTitle>
              </ListItem>
            ))}
          </PanelBody>
        </ListPanel>
        <TransferActions>
          <Button
            type="primary"
            onClick={moveSelectedToGroup}
            disabled={!selectedGroupId || selectedAvailableIds.length === 0}
          >
            &gt;&gt;
          </Button>
          <Button
            onClick={moveSelectedToAvailable}
            disabled={!selectedGroupId || selectedChosenIds.length === 0}
          >
            &lt;&lt;
          </Button>
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={saveDefinitionChanges}
            loading={isSavingDefinitions}
            disabled={!selectedGroupId || !hasPendingChanges}
          />
        </TransferActions>
        <ListPanel>
          <PanelHeader>Сонгогдсон бараанууд</PanelHeader>
          <PanelBody>
            {!selectedGroupId && <EmptyPanel>Алхам сонгоод бараа сонгоно уу.</EmptyPanel>}
            {selectedGroupId && selectedItems.length === 0 && <EmptyPanel>Сонгосон бараа байхгүй байна.</EmptyPanel>}
            {selectedGroupId && selectedItems.map((item) => {
              const selectedId = getSelectedId(item);
              return (
                <SelectedListItem
                  key={selectedId}
                  role="button"
                  tabIndex={0}
                  $selected={selectedChosenIdSet.has(selectedId)}
                  onClick={() => toggleSelection(selectedId, setSelectedChosenIds)}
                  onKeyDown={(event) =>
                    handleListItemKeyDown(event, selectedId, setSelectedChosenIds)
                  }
                >
                  <div>
                    <ListTitle>
                      <span>{item.masterName || item.name || "Нэргүй бараа"}</span>
                    </ListTitle>
                    <ListMeta>
                      <span>MI: {item.miMasterObjNum || "-"}</span>
                      {item.description && <span>{item.description}</span>}
                    </ListMeta>
                  </div>
                  <ItemAuditInfo>
                    <ItemAuditLine>
                      <span>Created:</span>
                      <span>{formatDateTime(getCreatedAt(item))}</span>
                    </ItemAuditLine>
                    <ItemAuditLine>
                      <span>Updated:</span>
                      <span>{formatDateTime(getUpdatedAt(item))}</span>
                    </ItemAuditLine>
                    <ItemAuditLine>
                      <span>Updated by:</span>
                      <span>{formatUser(getUpdatedBy(item))}</span>
                    </ItemAuditLine>
                  </ItemAuditInfo>
                  <ItemNumberInputs
                    onClick={(event) => event.stopPropagation()}
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <ItemNumberField>
                      <span>Нэмэлт Үнэ</span>
                      <InputNumber
                        min={0}
                        size="small"
                        value={getAdditionalPrice(item)}
                        onChange={(value) => updateSelectedItemValue(selectedId, value)}
                      />
                    </ItemNumberField>
                  </ItemNumberInputs>
                  <ListTitle>
                    <span className="type_blue_highlight">{item.type || item.categoryName || "-"}</span>
                  </ListTitle>
                </SelectedListItem>
              );
            })}
          </PanelBody>
        </ListPanel>
      </TransferWrap>
    </Wrap>
  );
};

const Wrap = styled.div`
  display: grid;
  gap: 18px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  overflow-x: auto;
  scrollbar-width: auto;
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(180px, 1fr)) auto;
  gap: 12px;
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  padding: 12px;
  border: 1px solid rgba(22, 119, 255, 0.18);
  box-shadow: 0 12px 28px rgba(22, 119, 255, 0.08);
  .ant-btn {
    padding: 0 18px;
    justify-self: start;
  }

  @media (max-width: 980px) {
    grid-template-columns: 1fr 1fr;
    .ant-btn {
      width: 100%;
    }
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  min-width: 0;
  .ant-select {
    width: 100%;
  }
`;

const TransferWrap = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 0.5fr) auto minmax(280px, 1fr);
  gap: 16px;
  align-items: stretch;
  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const TransferActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  position: sticky;
  top: 16px;
  align-self: start;
  .ant-btn {
    border-radius: 6px;
  }
  @media (max-width: 980px) {
    flex-direction: row;
    position: static;
  }
`;

const ListPanel = styled.div`
  border: 1px solid rgba(22, 119, 255, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 16px 32px rgba(22, 119, 255, 0.08);
  display: grid;
  grid-template-rows: auto auto 1fr;
  min-height: 360px;
`;

const PanelHeader = styled.div`
  padding: 12px 16px;
  background: linear-gradient(135deg, #1677ff, #4096ff);
  color: #ffffff;
  font-weight: 700;
  border-radius: 8px 8px 0 0;
  letter-spacing: 0.02em;
`;

const SearchInputWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f0f5ff;
  border-bottom: 1px solid rgba(22, 119, 255, 0.12);

  .search-icon {
    color: #1677ff;
    font-size: 14px;
    flex-shrink: 0;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 13px;
    color: #1f2937;
    padding: 4px 0;

    &::placeholder {
      color: #9ca3af;
    }
  }

  .clear-icon {
    color: #9ca3af;
    font-size: 14px;
    cursor: pointer;
    flex-shrink: 0;
    transition: color 0.2s;

    &:hover {
      color: #1677ff;
    }
  }
`;

const PanelBody = styled.div`
  padding: 12px 14px;
  display: grid;
  gap: 6px;
  overflow: auto;
  align-content: start;
`;

const EmptyPanel = styled.div`
  color: #6b7a88;
  font-size: 13px;
`;

const selectableItemStyles = css`
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${({ $selected }) => ($selected ? "#4096ff" : "rgba(148, 163, 184, 0.35)")};
  background: ${({ $selected }) => ($selected ? "rgba(22, 119, 255, 0.12)" : "rgba(255, 255, 255, 0.92)")};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, transform 0.15s ease;
  &:hover {
    border-color: rgba(22, 119, 255, 0.4);
    background: rgba(22, 119, 255, 0.08);
    transform: translateY(-1px);
  }
`;

const ListItem = styled.div`
  ${selectableItemStyles}
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SelectedListItem = styled(ListItem)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(170px, 220px) minmax(120px, 180px) auto;
  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr) minmax(170px, 220px) minmax(120px, 180px);
  }
  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr) minmax(120px, 180px);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ListTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1f2a33;

  span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .type_blue_highlight {
    color: #1677ff;
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;
  }
`;

const ListMeta = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  color: #6b7a88;
  overflow: hidden;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ItemAuditInfo = styled.div`
  display: grid;
  gap: 4px;
  min-width: 0;
`;

const ItemAuditLine = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 6px;
  align-items: center;
  font-size: 11px;
  line-height: 1.3;
  color: #6b7a88;
  span:last-child {
    color: #1f2a33;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ItemNumberInputs = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  align-items: end;
`;

const ItemNumberField = styled.div`
  display: grid;
  gap: 2px;
  max-width: 120px;
  font-size: 11px;
  color: #6b7a88;
  .ant-input-number {
    width: 100%;
  }
`;

const StepRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-top: 2px;
  padding-bottom: 4px;
  max-width: 100%;
  scrollbar-gutter: stable;
`;

const StepCard = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid ${({ $active, $ghost }) => ($active ? "rgba(22, 119, 255, 0.6)" : $ghost ? "rgba(22, 119, 255, 0.25)" : "rgba(148, 163, 184, 0.3)")};
  background: ${({ $active, $ghost }) => ($active ? "rgba(22, 119, 255, 0.14)" : $ghost ? "rgba(22, 119, 255, 0.08)" : "rgba(255, 255, 255, 0.92)")};
  color: #1f2a33;
  font-weight: 600;
  min-width: 160px;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: rgba(22, 119, 255, 0.65);
    background: rgba(22, 119, 255, 0.12);
    transform: translateY(-1px);
  }
`;

const StepCardDashed = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 6px;
  border: 2px dashed ${({ $active, $ghost }) => ($active ? "rgba(22, 119, 255, 0.6)" : $ghost ? "rgba(22, 119, 255, 0.25)" : "rgba(148, 163, 184, 0.3)")};
  background: ${({ $active, $ghost }) => ($active ? "rgba(22, 119, 255, 0.14)" : $ghost ? "rgba(22, 119, 255, 0.08)" : "rgba(255, 255, 255, 0.92)")};
  color: #1f2a33;
  font-weight: 600;
  min-width: 240px;
  justify-content: space-between;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: rgba(22, 119, 255, 0.65);
    background: rgba(22, 119, 255, 0.12);
    transform: translateY(-1px);
  }
`;

const StepInfo = styled.div`
  display: grid;
  gap: 4px;
`;

const StepDelete = styled.button`
  border: 1px solid rgba(239, 68, 68, 0.4);
  background: rgba(239, 68, 68, 0.08);
  color: #b91c1c;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  &:hover {
    border-color: rgba(239, 68, 68, 0.7);
    background: rgba(239, 68, 68, 0.16);
    transform: translateY(-1px);
  }
`;

const StepForm = styled.div`
  display: grid;
  grid-template-columns: minmax(30px, 80px) minmax(30px, 80px) auto;
  align-items: center;
  gap: 8px;
`;

const StepBadge = styled.span`
  height: 26px;
  padding-left: 5px;
  padding-right: 5px;
  border-radius: 999px;
  border: 2px solid rgba(22, 119, 255, 0.4);
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
  color: #1677ff;
`;

export default ConfigTab;
